<template>
  <a-select
    :getPopupContainer="(triggerNode) => triggerNode.parentNode"
    class="select"
    mode="multiple"
    :value="value"
    :max-tag-count="0"
    :options="options"
    :disabled="disabled"
    :showSearch="false"
    showArrow
    placeholder="Seleccionar identificadores"
  >
    <template #maxTagPlaceholder="omittedValues">
      <div class="items-counter-box">
        {{ omittedValues.length }}
      </div>

      <span v-if="omittedValues.length === options.length">Todos los identificadores</span>
      <span v-else-if="omittedValues.length === 1">
        {{ currentOption?.label }}
        <span class="label label__alias" v-if="currentOption?.alias">
          ({{ currentOption.alias }})
        </span>
      </span>
      <span v-else>Identificadores seleccionados</span>
    </template>

    <template #dropdownRender>
      <div id="channel-id-multi-picker" @mousedown.prevent @click.stop>
        <a-checkbox
          :checked="allSelected"
          :indeterminate="indeterminate"
          @change="handleChangeAllSelected"
        >
          <span class="label" @click.prevent="handleChangeAllSelected">Seleccionar todos</span>
        </a-checkbox>

        <a-divider type="horizontal" class="divider" />

        <a-checkbox-group class="checkbox-group" :value="value" @update:value="handleUpdate">
          <a-checkbox v-for="opt in options" :key="opt.value" :value="opt.value">
            <span class="label" @click.prevent="handleClick(opt.value)">
              {{ opt.label }}
              <span class="label__alias" v-if="opt.alias">({{ opt.alias }})</span>
            </span>
          </a-checkbox>
        </a-checkbox-group>
      </div>
    </template>
  </a-select>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { cloneDeep } from "lodash";

import { ChannelCustomer } from "@/@types/global";
import { fetchAllChannelsCustomers } from "@/app/channels/services";
import { ChannelType } from "@/app/shared/utils/enums";

const props = withDefaults(defineProps<{
  channelType: (typeof ChannelType)[keyof typeof ChannelType];
  value: number[];
  disabled?: boolean;
  includeDeleted?: boolean;
  showDeleted?: boolean;
}>(), {
  includeDeleted: false,
  showDeleted: false
});

const emit = defineEmits<{
  "update:value": [newValue: number[]];
  "select-with-deleted": [newValue: number[]];
}>();

const channelCustomers = ref<ChannelCustomer[]>([]);

const options = computed(() => {
  const channelCustomersToShow = props.showDeleted ? 
    channelCustomers.value : 
    channelCustomers.value.filter((customer) => customer.status != 3);

  const identifiers = channelCustomersToShow.map((channel) => ({
    label: channel.associate,
    alias: channel.alias,
    value: channel.id,
  }));

  return identifiers;
});

const currentOption = computed(() => {
  if (props.value.length !== 1) return undefined;

  return options.value.find((opt) => opt.value === props.value[0]);
});

const allSelected = ref(true);
const indeterminate = ref(false);

watch(
  () => props.value,
  (newValue) => {
    indeterminate.value = newValue.length === 0 ? false : newValue.length !== options.value.length;

    if (indeterminate.value) allSelected.value = false;
    if (newValue.length === options.value.length) allSelected.value = true;
  }
);

onMounted(async () => {
  console.log("fetching channel customers with includeDeleted:", props.includeDeleted);
  channelCustomers.value = await fetchAllChannelsCustomers(props.channelType, undefined, undefined, props.includeDeleted);

  if (allSelected.value) handleUpdate(options.value.map((opt) => opt.value));
});

const handleChangeAllSelected = () => {
  if (indeterminate.value) {
    allSelected.value = true;
    indeterminate.value = false;
  } else allSelected.value = !allSelected.value;

  const newValue = allSelected.value ? options.value.map((opt) => opt.value) : [];

  handleUpdate(newValue);
};

const handleClick = (value: number) => {
  const valueCopy = cloneDeep(props.value);

  if (valueCopy.includes(value)) {
    const index = valueCopy.findIndex((v) => v === value);

    valueCopy.splice(index, 1);
  } else valueCopy.push(value);

  handleUpdate(valueCopy);
};

const handleUpdate = (newValue: number[]) => {
  emit("update:value", newValue);
  if (newValue.length === options.value.length) {
    const allValues = channelCustomers.value.map((channel) => channel.id);
    emit("select-with-deleted", allValues);
  } else {
    emit("select-with-deleted", newValue);
  }
};
</script>

<style scoped lang="sass">
.select
  min-width: 260px

.divider
  background-color: $gray-4
  margin: 0 auto
  min-width: 0
  max-width: calc(100% - 14px)

.label
  display: inline-block
  padding: 5px 8px
  font-size: 14px
  font-weight: 400
  color: $gray-8

  &__alias
    color: $gray-7

#channel-id-multi-picker
  display: flex
  flex-direction: column
  max-height: 300px
  overflow-y: auto

.checkbox-group
  display: flex
  flex-direction: column
  width: 100%
  gap: 0px

.ant-checkbox-wrapper
  // padding: 5px 12px

  &:hover
    background-color: $blue-1

:deep(.ant-checkbox-wrapper .ant-checkbox)
  margin-left: 12px

:deep(.ant-select-selection-item)
  background-color: transparent
  border: none
  margin: 0
  padding: 0

:deep(.ant-select-selection-item-content)
  font-size: 14px
  color: $gray-8
  display: flex
  align-items: center
  gap: 6px

.items-counter-box
  border-radius: 20px
  border: 1px solid $gray-5
  background-color: $gray-3
  margin-left: 2px
  min-width: 30px
  display: flex
  justify-content: center
  color: $gray-8
  font-size: 12px
</style>

<style>
#channel-id-multi-picker .ant-checkbox + span {
  padding: 0;
  flex: 1 1 auto;
}

#channel-id-multi-picker .ant-checkbox-wrapper + .ant-checkbox-wrapper {
  margin: 0;
}
</style>
